import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Text,
  Title,
  Group,
  ActionIcon,
  Badge,
  Button,
  TextInput,
  UnstyledButton,
  Menu,
  Divider,
  ScrollArea,
  Table,
  FileButton,
  Loader,
  Center,
  Stack,
  Paper,
  Flex,
  Tooltip,
  Modal,
  Select,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import {
  IconFolder,
  IconFolderOpen,
  IconFolderPlus,
  IconRefresh,
  IconDotsVertical,
  IconTrash,
  IconPencil,
  IconHome,
  IconChevronRight,
  IconChevronDown,
  IconUpload,
  IconList,
  IconLayoutGrid,
  IconLock,
  IconShare,
  IconDownload,
  IconExclamationMark,
  IconFile,
  IconCheck,
} from "@tabler/icons-react";
import { Dropzone } from "@mantine/dropzone";
import moment from "moment";
import _ from "lodash";
import { useServerApi } from "../hooks/useServerApi";
import {
  getFileIcon,
  getFileIconColor,
  isImageFile,
} from "../components/fileList";

const filesize = require("file-size");

// ─── Folder Tree Node ────────────────────────────────────────────────────────

const FolderNode = ({
  folder,
  depth = 0,
  selectedFolderId,
  onSelect,
  onRename,
  onDelete,
  onCreateChild,
}) => {
  const [expanded, setExpanded] = useState(depth === 0);
  const hasChildren = folder.children && folder.children.length > 0;
  const isSelected = selectedFolderId === folder._id;

  return (
    <>
      <Group
        gap={4}
        px={8}
        py={4}
        wrap="nowrap"
        style={{
          paddingLeft: 8 + depth * 16,
          cursor: "pointer",
          borderRadius: 4,
          background: isSelected ? "rgba(34,139,230,0.12)" : "transparent",
        }}
        onMouseEnter={(e) => {
          if (!isSelected)
            e.currentTarget.style.background = "rgba(0,0,0,0.05)";
        }}
        onMouseLeave={(e) => {
          if (!isSelected) e.currentTarget.style.background = "transparent";
        }}
      >
        <UnstyledButton
          style={{ flex: 1 }}
          onClick={() => {
            onSelect(folder);
            if (hasChildren) setExpanded((v) => !v);
          }}
        >
          <Group gap={6} wrap="nowrap">
            {hasChildren ? (
              expanded ? (
                <IconChevronDown size={14} color="#888" />
              ) : (
                <IconChevronRight size={14} color="#888" />
              )
            ) : (
              <Box w={14} />
            )}
            {isSelected ? (
              <IconFolderOpen size={18} color="#2BBC8A" />
            ) : (
              <IconFolder size={18} color="#2BBC8A" />
            )}
            <Text size="sm" lineClamp={1} style={{ flex: 1 }}>
              {folder.name}
            </Text>
            {folder.count > 0 && (
              <Badge size="xs" color="blue" variant="filled" circle={false}>
                {folder.count}
              </Badge>
            )}
          </Group>
        </UnstyledButton>

        <Menu shadow="md" width={160} withinPortal>
          <Menu.Target>
            <ActionIcon
              size="xs"
              variant="subtle"
              onClick={(e) => e.stopPropagation()}
            >
              <IconDotsVertical size={14} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              icon={<IconFolderPlus size={14} />}
              onClick={() => onCreateChild(folder._id)}
            >
              New Subfolder
            </Menu.Item>
            <Menu.Item
              icon={<IconPencil size={14} />}
              onClick={() => onRename(folder)}
            >
              Rename
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              icon={<IconTrash size={14} />}
              color="red"
              onClick={() => onDelete(folder)}
            >
              Delete
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {expanded &&
        hasChildren &&
        folder.children.map((child) => (
          <FolderNode
            key={child._id}
            folder={child}
            depth={depth + 1}
            selectedFolderId={selectedFolderId}
            onSelect={onSelect}
            onRename={onRename}
            onDelete={onDelete}
            onCreateChild={onCreateChild}
          />
        ))}
    </>
  );
};

// ─── File Row ─────────────────────────────────────────────────────────────────

const FileRow = ({ file, onDelete, onOpen }) => {
  const iconDef = getFileIcon(file.mimetype);
  const color = getFileIconColor(file.mimetype);
  const Icon = iconDef.c;

  return (
    <Table.Tr>
      <Table.Td style={{ minWidth: 250 }}>
        <Group gap="xs" wrap="nowrap">
          <Icon size={18} color={color} />
          <UnstyledButton onClick={() => onOpen(file)}>
            <Text size="xs">{file.name}</Text>
          </UnstyledButton>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="xs">
          {file.size ? filesize(file.size, { fixed: 0 }).human("si") : "--"}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="xs">
          {file.created ? moment(file.created).format("LLL") : "--"}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <ActionIcon
            variant="default"
            size="sm"
            onClick={() => onOpen(file)}
            title="Download / Open"
          >
            <IconDownload size={14} />
          </ActionIcon>
          <ActionIcon
            variant="default"
            size="sm"
            color="red"
            onClick={() => onDelete(file)}
            title="Delete"
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};

// ─── File Panel ───────────────────────────────────────────────────────────────

const FilePanel = ({ folder, onRefreshTree }) => {
  const [api] = useServerApi();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const resetRef = useRef(null);
  const [shareOpen, { open: openShare, close: closeShare }] =
    useDisclosure(false);
  const [shareUserId, setShareUserId] = useState("");
  const [sharePermission, setSharePermission] = useState("Read Only");

  const fetchFiles = async () => {
    if (!folder) return;
    const data = await api.FileManager.getFiles(folder._id);
    if (data) setFiles(data);
  };

  useEffect(() => {
    fetchFiles();
  }, [folder?._id]);

  const handleUpload = async (uploadFiles) => {
    if (!folder) return;
    setUploading(true);
    try {
      for (const file of uploadFiles) {
        const result = await api.FileManager.uploadFile(folder._id, file);
        if (result?.success) {
          showNotification({ title: "Uploaded", message: file.name });
        } else {
          showNotification({
            title: "Upload Failed",
            message: file.name,
            color: "red",
            icon: <IconExclamationMark size={18} />,
          });
        }
      }
      await fetchFiles();
      onRefreshTree();
    } finally {
      setUploading(false);
      resetRef.current?.();
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Delete "${file.name}"?`)) return;
    const result = await api.FileManager.deleteFile(file._id);
    if (result !== undefined) {
      setFiles((prev) => prev.filter((f) => f._id !== file._id));
      onRefreshTree();
      showNotification({ title: "Deleted", message: file.name });
    }
  };

  const handleOpen = (file) => {
    if (file.url) window.open(file.url, "_blank");
  };

  const totalSize = files.reduce((sum, f) => sum + (f.size || 0), 0);

  if (!folder) {
    return (
      <Center h={400}>
        <Stack align="center" gap="sm">
          <IconFolder size={48} color="#ccc" />
          <Text size="sm" c="dimmed">
            Select a folder to view files
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Box>
      {/* Folder header */}
      <Group justify="space-between" mb="md">
        <Title order={4}>{folder.name}</Title>
        <Group gap="xs">
          <Tooltip label="Refresh">
            <ActionIcon variant="default" onClick={fetchFiles}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Lock">
            <ActionIcon variant="default">
              <IconLock size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Share">
            <ActionIcon variant="default" onClick={openShare}>
              <IconShare size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* Metadata badges */}
      <Group gap="xs" mb="md">
        <Badge color="blue" variant="filled" size="sm">
          PUBLIC
        </Badge>
        <Badge color="gray" variant="light" size="sm">
          {files.length} {files.length === 1 ? "FILE" : "FILES"}
        </Badge>
        {totalSize > 0 && (
          <Badge color="gray" variant="light" size="sm">
            {filesize(totalSize, { fixed: 1 }).human("si")}
          </Badge>
        )}
      </Group>

      {/* Upload zone */}
      <Dropzone
        onDrop={handleUpload}
        onReject={(f) => console.log("rejected", f)}
        mb="md"
        styles={{ root: { border: "1.5px dashed #444", borderRadius: 8 } }}
      >
        <FileButton resetRef={resetRef} onChange={handleUpload} multiple>
          {(props) => (
            <Button {...props} size="sm" leftSection={<IconUpload size={16} />}>
              Upload Files
            </Button>
          )}
        </FileButton>
      </Dropzone>

      {/* File list */}
      {uploading && (
        <Center mb="md">
          <Loader size="sm" />
        </Center>
      )}
      {files.length === 0 && !uploading ? (
        <Text size="xs" c="dimmed">
          No files uploaded yet.
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Text size="xs">File</Text>
              </Table.Th>
              <Table.Th>
                <Text size="xs">Size</Text>
              </Table.Th>
              <Table.Th>
                <Text size="xs">Date</Text>
              </Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {files.map((file) => (
              <FileRow
                key={file._id}
                file={file}
                onDelete={handleDelete}
                onOpen={handleOpen}
              />
            ))}
          </Table.Tbody>
        </Table>
      )}

      {/* Share Folder Modal */}
      <Modal
        opened={shareOpen}
        onClose={closeShare}
        title="Share Folder"
        size="md"
        radius="md"
      >
        <TextInput
          label="User ID"
          withAsterisk
          placeholder="Enter user ID to share with"
          value={shareUserId}
          onChange={(e) => setShareUserId(e.currentTarget.value)}
          mb="md"
          autoFocus
        />
        <Select
          label="Permission"
          value={sharePermission}
          onChange={(val) => setSharePermission(val)}
          data={["Read Only", "Read & Write", "Admin"]}
          mb="xl"
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={closeShare}>
            Cancel
          </Button>
          <Button
            leftSection={<IconCheck size={16} />}
            disabled={!shareUserId.trim()}
            onClick={() => {
              showNotification({
                title: "Folder shared",
                message: `Shared "${folder?.name}" with ${shareUserId} (${sharePermission})`,
                icon: <IconCheck size={16} />,
              });
              setShareUserId("");
              setSharePermission("Read Only");
              closeShare();
            }}
          >
            Share Folder
          </Button>
        </Group>
      </Modal>
    </Box>
  );
};

// ─── Main File Manager Page ───────────────────────────────────────────────────

const FileManager = () => {
  const [api] = useServerApi();
  const [tree, setTree] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderParentId, setNewFolderParentId] = useState(null);
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [folderModalOpen, { open: openFolderModal, close: closeFolderModal }] =
    useDisclosure(false);
  const [renameModalOpen, { open: openRenameModal, close: closeRenameModal }] =
    useDisclosure(false);

  const fetchTree = async () => {
    const data = await api.FileManager.getTree();
    if (data?.tree) setTree(data.tree);
  };

  useEffect(() => {
    fetchTree();
  }, []);

  const handleSelectFolder = (folder) => {
    setSelectedFolder(folder);
  };

  const handleOpenCreateFolder = (parentId = null) => {
    setNewFolderParentId(parentId);
    setNewFolderName("");
    openFolderModal();
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    const folder = await api.FileManager.createFolder({
      name: newFolderName.trim(),
      parentId: newFolderParentId,
    });
    if (folder) {
      await fetchTree();
      showNotification({ title: "Folder created", message: folder.name });
    }
    closeFolderModal();
  };

  const handleRenameFolder = (folder) => {
    setRenamingFolder(folder);
    setRenameValue(folder.name);
    openRenameModal();
  };

  const handleConfirmRename = async () => {
    if (!renameValue.trim() || !renamingFolder) return;
    const updated = await api.FileManager.updateFolder(renamingFolder._id, {
      name: renameValue.trim(),
    });
    if (updated) {
      await fetchTree();
      if (selectedFolder?._id === renamingFolder._id) {
        setSelectedFolder((prev) => ({ ...prev, name: renameValue.trim() }));
      }
    }
    closeRenameModal();
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}" and all its contents?`))
      return;
    await api.FileManager.deleteFolder(folder._id);
    await fetchTree();
    if (selectedFolder?._id === folder._id) setSelectedFolder(null);
    showNotification({ title: "Folder deleted", message: folder.name });
  };

  return (
    <Box p="xl">
      <Title mb="xl">File Managers</Title>

      <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
        <Flex style={{ minHeight: 600 }}>
          {/* Left: Folder sidebar */}
          <Box
            w={260}
            style={{ borderRight: "1px solid #2a2a2a", flexShrink: 0 }}
            p="sm"
          >
            <Group justify="space-between" mb="sm">
              <Text fw={600} size="sm">
                Folders
              </Text>
              <Group gap={4}>
                <Tooltip label="Refresh">
                  <ActionIcon variant="subtle" size="sm" onClick={fetchTree}>
                    <IconRefresh size={15} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="New Folder">
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={() => handleOpenCreateFolder(null)}
                  >
                    <IconFolderPlus size={15} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>

            {/* Home / All */}
            <UnstyledButton
              w="100%"
              px={8}
              py={4}
              mb={4}
              style={{
                borderRadius: 4,
                background:
                  selectedFolder === null
                    ? "rgba(34,139,230,0.12)"
                    : "transparent",
              }}
              onClick={() => setSelectedFolder(null)}
            >
              <Group gap={8}>
                <IconHome size={16} color="#888" />
                <Text size="sm">Home</Text>
              </Group>
            </UnstyledButton>

            <Divider mb="sm" />

            <ScrollArea style={{ flex: 1 }}>
              {tree.length === 0 ? (
                <Text size="xs" c="dimmed" px={8}>
                  No folders yet.
                </Text>
              ) : (
                tree.map((folder) => (
                  <FolderNode
                    key={folder._id}
                    folder={folder}
                    depth={0}
                    selectedFolderId={selectedFolder?._id}
                    onSelect={handleSelectFolder}
                    onRename={handleRenameFolder}
                    onDelete={handleDeleteFolder}
                    onCreateChild={handleOpenCreateFolder}
                  />
                ))
              )}
            </ScrollArea>
          </Box>

          {/* Right: File panel */}
          <Box p="md" style={{ flex: 1, overflow: "auto" }}>
            <FilePanel folder={selectedFolder} onRefreshTree={fetchTree} />
          </Box>
        </Flex>
      </Paper>

      {/* Create Folder Modal */}
      <Modal
        opened={folderModalOpen}
        onClose={closeFolderModal}
        title="New Folder"
        size="sm"
      >
        <TextInput
          label="Folder Name"
          placeholder="e.g. GI, Life, MPF..."
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
          mb="md"
          autoFocus
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={closeFolderModal}>
            Cancel
          </Button>
          <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()}>
            Create
          </Button>
        </Group>
      </Modal>

      {/* Rename Folder Modal */}
      <Modal
        opened={renameModalOpen}
        onClose={closeRenameModal}
        title="Rename Folder"
        size="sm"
      >
        <TextInput
          label="New Name"
          value={renameValue}
          onChange={(e) => setRenameValue(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && handleConfirmRename()}
          mb="md"
          autoFocus
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={closeRenameModal}>
            Cancel
          </Button>
          <Button onClick={handleConfirmRename} disabled={!renameValue.trim()}>
            Save
          </Button>
        </Group>
      </Modal>
    </Box>
  );
};

export default FileManager;
